import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function ButikkensHistorie() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Butikkens historie - Havneloppen";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Læs historien om Havneloppen in Nyborg. Fra en lille drøm om genbrug og fællesskab til et levende loppemarked fyldt med sjæl, hygge og gode fund."
      );
    }

    fetch("https://api.dev.auxo.dk/public/content/sider")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const page = data.find((p) => p.url_slug === "historie");
          setPageData(page);
        }
      })
      .catch((err) => console.error("Error fetching history page data:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange" />
      </div>
    );
  }

  const historyImage = pageData?.billede?.[0] || "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/631586353-1484986060296776-2960624181118375474-n.jpg";
  const optimizedHistoryImage = historyImage.replace(
    "assets.auxodev.dk/assets",
    "assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets"
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-brand-blue text-warm-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHistoryImage}
            alt={pageData?.titel || "Butikkens historie"}
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange mb-4">
            <Link to="/" className="hover:text-warm-white transition-colors">FORSIDE</Link>
            <span className="text-stone-400">/</span>
            <span className="text-warm-white">BUTIKKENS HISTORIE</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-warm-white sm:text-4xl md:text-5xl">
            {pageData?.titel || "Butikkens historie"}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16 w-full">
        <div className="space-y-8">
          {/* 2-Column Grid for Image and Story Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Column 1: Large Atmospheric Image */}
          <div className="lg:col-span-5 aspect-[4/3] lg:aspect-[3/4] overflow-hidden rounded-2xl border border-warm-beige shadow-md lg:sticky lg:top-24">
            <img
              src={optimizedHistoryImage}
              alt="Historisk billede af Havneloppen"
              className="h-full w-full object-cover"
            />
          </div>

          {/* Column 2: Story Content */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-warm-beige p-6 md:p-10 shadow-sm">
            <div
              className="prose max-w-none text-dark-graphite-light"
              dangerouslySetInnerHTML={{ __html: pageData?.indhold || "" }}
            />
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center pt-6">
          <h3 className="font-display text-xl font-bold text-brand-blue mb-4">
            Vil du være en del af vores historie?
          </h3>
          <p className="text-sm text-dark-graphite-light max-w-md mx-auto mb-6">
            Kig forbi butikken på Knudshovedvej 38 i Nyborg og gør dit næste unikke fund, eller kontakt os hvis du har loppefund, du vil sælge.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/kategorier"
              className="rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-warm-white hover:bg-brand-orange-hover transition-colors shadow-sm"
            >
              Se vores kategorier
            </Link>
            <Link
              to="/kontakt"
              className="rounded-lg border border-brand-blue px-5 py-2.5 text-sm font-bold text-brand-blue hover:bg-brand-blue/5 transition-colors"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
);
}
